import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { Card, CardBody, CardHeader, Flex, Link, Text } from '@backstage/ui';

import { useAuth } from '../../../../../behaviors/auth-factory';
import { useSupportMessenger } from '../../../../../hooks/useSupportMessenger';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  fillViewport: {
    minHeight: '60vh',
  },
  description: {
    textAlign: 'center',
    maxWidth: '520px',
  },
  cards: {
    maxWidth: '720px',
  },
  link: {
    display: 'inline',
    textDecoration: 'underline',
  },
}));

const COPY_BY_VARIANT = {
  page: {
    title: 'Page not found',
    description:
      "Oops! Looks like you've ventured off the beaten path. The page you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
  resource: {
    title: 'Resource not found',
    description:
      "Oops! The resource you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
} as const;

export type NotFoundContentProps = {
  variant?: 'page' | 'resource';
  fillViewport?: boolean;
};

export const NotFoundContent = ({
  variant = 'page',
  fillViewport = true,
}: NotFoundContentProps = {}) => {
  const classes = useStyles();
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const { title, description } = COPY_BY_VARIANT[variant];

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <div
      className={classNames(
        classes.container,
        fillViewport && classes.fillViewport,
      )}
    >
      <Flex justify="center" direction="column" gap="6">
        <Flex direction="column" align="center">
          <Text variant="title-x-small" color="secondary">
            Error 404
          </Text>
          <Text variant="title-large" as="h2">
            {title}
          </Text>
        </Flex>
        <Flex direction="column" mb="8" align="center">
          <Text variant="body-large" as="p" className={classes.description}>
            {description}
          </Text>
        </Flex>
        <Flex direction="row" gap="6" className={classes.cards}>
          <Card>
            <CardHeader>Double-check the URL</CardHeader>
            <CardBody>
              <Text variant="body-medium" color="secondary" as="p">
                Sometimes a simple typo is the culprit
              </Text>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>Start fresh</CardHeader>
            <CardBody>
              <Text variant="body-medium" color="secondary" as="p">
                <Link href="/" className={classes.link}>
                  Go back to the homepage
                </Link>{' '}
                and find what you need
              </Text>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>Need help?</CardHeader>
            <CardBody>
              <Text variant="body-medium" color="secondary" as="p">
                If you think this is an error, please{' '}
                <Link
                  className={classes.link}
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleContactSupport();
                  }}
                >
                  contact our support team
                </Link>
              </Text>
            </CardBody>
          </Card>
        </Flex>
      </Flex>
    </div>
  );
};
